import {
  Component,
  Inject,
  OnInit,
  ChangeDetectorRef,
  ChangeDetectionStrategy,
  ViewChild,
} from '@angular/core';
import {
  FileEntryCategory,
  FileEntryEntity,
  FileEntryMethod,
  FileEntryWebstatus,
} from '@wephone-core/model/entity/fileentry';
import { FileEntryService } from '@wephone-core/service/file_entry_service';
import {
  DialogActionButton,
  Colors,
  regexSearch,
  DialogService,
  ToastService,
  LoggerService,
} from '@wephone-utils';
import * as _ from 'lodash';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { FormControl } from '@angular/forms';
import { EntityManager, FlexIvrSettings } from '@wephone-core/wephone-core.module';
import { FileEntryRepository } from '@wephone-core/model/repository/fileentry';
import { DialogComponentBase } from '@wephone-core-ui';
import { _tk, _ti } from '@wephone-translation';
import { LocalManager } from '@wephone-core/service/local_manager';
import { UserProfileService } from '@wephone/services/user-profile.service';
// import { TranslateService } from '@ngx-translate/core';
import { AuthenticationService } from '@wephone-core/service/authentication';

enum ChangeSoundMethod {
  TTS = 'text_to_speech',
  UPLOAD = 'upload_file'
}

enum StepChangeSound {
  SELECT_SOUND = 'select_sound',
  ADD_SOUND = 'add_sound',
  CONFIRM_SOUND = 'confirm_sound',
}

interface FileEntryOptions {
  name?: string;
  is_temp?: number;
  creation_method?: number;
  file_entry_id?: number;
  category?: FileEntryCategory;
}

@Component({
  selector: 'manage-ringtones',
  templateUrl: './manage-ringtones.html',
  styleUrls: ['./manage-ringtones.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ManageRingtonesModal extends DialogComponentBase implements OnInit {
  static UnabledPlayedType: string[] = [
    'audio/x-m4a',
    'audio/m4a',
  ];

  @ViewChild('audioPlayer') audioPlayer;
  dialogTitle = _tk('manage_ringtones.content.title');
  backButton: DialogActionButton =
    {
      label: _tk('public.navigation.back'),
      action: () => {
        this.goPrevStep();
      },
      visible: () => {
        return this.steps.indexOf(this.step) > 0;
      },
    };
  dialogRightActions: DialogActionButton[] = [
    {
      label: _tk('public.finish'),
      action: () => {
        this.save();
      },
      visible: () => {
        return this.step === this.steps[this.steps.length - 1];
      },
      color: Colors.PRIMARY
    }
  ];

  private readonly fileEntryRepo = EntityManager.getRepository<FileEntryRepository>('FileEntryRepository');

  showBtnSelects: any = [];
  steps: StepChangeSound[] = [
    StepChangeSound.SELECT_SOUND,
    StepChangeSound.ADD_SOUND,
    StepChangeSound.CONFIRM_SOUND,
  ];

  step = StepChangeSound.SELECT_SOUND;
  selectedMethod: string;

  // file uploaded
  soundFile: any;

  allowedMaxSize: number;

  // binding from directive
  showInputName = true; // Check whether showing input name or not, default as True
  markFileAsTemp = true; // Update is_temp file-entry after saving file-entry
  langSearch = '';

  // new ringtone which will be created
  newFileEntry: FileEntryEntity;
  newFileEntryOptions: FileEntryOptions = {
    category: FileEntryCategory.RECORDING_CALL,
  };
  searchCtrl = new FormControl();
  ringtones: FileEntryEntity[];
  filteredRingtones: FileEntryEntity[];
  fileEntrySelect = new FormControl();
  errors: any = {};
  fileDefaultSound: any;
  language: string;
  allowedUploadExtensions: string[] = [
    'mp3',
    'wav',
    'm4a',
  ];

  constructor(
    dialogRef: MatDialogRef<ManageRingtonesModal>,
    @Inject(MAT_DIALOG_DATA) data: any,
    private readonly flexIvrSettings: FlexIvrSettings,
    private readonly fileEntryService: FileEntryService,
    private readonly cdRef: ChangeDetectorRef,
    private readonly localeManager: LocalManager,
    private readonly profileService: UserProfileService,
    private readonly dialogService: DialogService,
    private readonly toast: ToastService,
    private readonly authService: AuthenticationService,
  ) {
    super(dialogRef, cdRef);
    this.loadRingtones();
  }

  ngOnInit(): void {
    super.ngOnInit();

    this.allowedMaxSize = this.flexIvrSettings.getMaxUploadSize('audio');
    this.language = this.localeManager.getLangForDefaultFileEntry();

    this.addSubscription(
      this.searchCtrl.valueChanges.subscribe(value => {
        const filterValue: string = (value || '').trim();
        this.filterSound(filterValue);
      })
    );
  }

  async loadRingtones(): Promise<void> {
    this.ringtones = await this.profileService.getRingtones();
    this.filteredRingtones = this.ringtones;
    this.detectChanges();
  }

  canPlayLocalFile(): boolean {
    return this.soundFile && !_.includes(ManageRingtonesModal.UnabledPlayedType, this.soundFile.type);
  }

  getDefaultSoundName(rootName: string): string {
    let name = rootName;
    let count = 0;
    while (true) {
      count += 1;
      if (!this.ringtones.find(x => x.name === name)) {
        break;
      }
      name = `${rootName} ${count}`;
    }
    return name;
  }

  filterSound(name: string): void {
    this.filteredRingtones = this.ringtones.filter(sound => {
      if (!name) {
        return true;
      }
      return regexSearch(sound.name, name);
    });
    this.updateDialogLayout();
  }

  goPrevStep(): void {
    if (this.step === this.steps[0]) {
      return;
    }
    this.step = this.steps[this.steps.indexOf(this.step) - 1];
    this.markAsChanged();
  }

  async goNextStep(): Promise<void> {
    if (this.step === this.steps[this.steps.length - 1]) {
      this.loggerService.warn('End step has been reached');
      return;
    }
    this.step = this.steps[this.steps.indexOf(this.step) + 1];
    this.markAsChanged();
  }

  addSound(): void {
    this.newFileEntryOptions.name = this.getDefaultSoundName(_ti('reusable_sound.content.name'));
    this.goNextStep();
  }

  private getSoundNameError(): string {
    const currentUser = this.authService.getUser();
    const soundName: string = this.newFileEntryOptions.name;
    if (!soundName || !soundName.trim()) {
      return 'sound_manager.message.name_is_empty';
    }

    if (this.fileEntryRepo.ringtoneExist(currentUser.id, soundName)) {
      return 'public.message.name_exist';
    }
  }

  private setNewFileEntry(fileentry: FileEntryEntity): void {
    this.newFileEntry = fileentry;
    if (!this.newFileEntry) {
      this.loggerService.error('No file entry responsed');
      return;
    }
    this.newFileEntry.setRemoteUrl(this.fileEntryService.getFileUrl(this.newFileEntry.public_id, false, fileentry.is_default ? this.language : null));
    this.markAsChanged();
  }

  private async uploadRingtone(): Promise<FileEntryEntity> {
    if (!this.soundFile) {
      this.loggerService.error('No local sound to upload');
      throw new Error(_ti('sound_manager.message.no_uploaded_file'));
    }
    const newFileEntryOptions: any = _.cloneDeep(this.newFileEntryOptions);
    newFileEntryOptions.creation_method = FileEntryMethod.UPLOAD;
    newFileEntryOptions.category = FileEntryCategory.RINGTONE;

    const resp: any = await this.fileEntryService.upload(this.soundFile, newFileEntryOptions);
    this.fileEntryRepo.setDataAsReady();
    const file_entry: FileEntryEntity = this.fileEntryRepo.getObjectById(resp.id);
    this.setNewFileEntry(file_entry);

    return this.newFileEntry;
  }

  fileLoadedForBrowser(soundFile: any): void {
    // this.loggerService.log('soundFile', soundFile);
    this.selectedMethod = ChangeSoundMethod.UPLOAD;
    this.soundFile = soundFile;
    this.markAsChanged();
    this.goNextStep();
  }

  async save(): Promise<void> {
    this.errors = {};
    try {
      if (!this.canPublishFileEntry()) {
        this.loggerService.error('No new file entry to be uploaded or created');
        throw new Error(_ti('sound_manager.message.no_uploaded_file'));
      }

      const soundNameError: string = this.getSoundNameError();
      if (soundNameError) {
        this.errors.name = soundNameError;
        throw new Error(_ti(soundNameError));
      }

      switch (this.selectedMethod) {
        case ChangeSoundMethod.UPLOAD:
          await this.uploadRingtone();
          this.loadRingtones();
          break;

        default:
          throw new Error('No method selected');
      }
      this.step = StepChangeSound.SELECT_SOUND;
      this.markAsChanged();
      this.toast.show(_ti('public.message.create_success'));
    } catch (error) {
      this.toastService.showErrorMessage(error, _ti('fileentry.error.unknown'));
      return Promise.reject();
    } finally {
      this.markAsChanged();
    }
  }

  chooseExisting(fileEntry: FileEntryEntity): void {
    this.dismiss({ file_entry: fileEntry });
  }

  canPublishFileEntry(): boolean {
    return this.selectedMethod === ChangeSoundMethod.UPLOAD && this.soundFile;
  }

  closeDialog = () => {
    if (!this.newFileEntry) {
      this.loggerService.error('No new file entry created');
      this.toastService.showError(_ti('sound_manager.message.no_uploaded_file'));

      return;
    }
    // this.loggerService.log('new file entry created', this.newFileEntry);
    this.dismiss({ file_entry: this.newFileEntry });
  }

  deleteSound(): void {
    this.stopSound();
    this.soundFile = undefined;
    this.goPrevStep();
  }

  stopSound(): void {
    if (this.audioPlayer) {
      this.audioPlayer.stop();
    }
  }

  markAsChanged(): void {
    this.cdRef.markForCheck();
    this.updateDialogLayout();
  }

  disablePlaying(file: FileEntryEntity): boolean {
    if (file.web_status === FileEntryWebstatus.AVAIL_BOTH || file.web_status === FileEntryWebstatus.AVAIL_AWS_S3) {
      return false;
    }
    return true;
  }

  async deleteRingtone(fileEntry: FileEntryEntity): Promise<any> {
    return this.dialogService.confirmDialog(
      _ti('dialogs.confirmation'),
      _ti('user.title.delete'),
      async () => {
        if (!fileEntry) {
          return;
        }

        try {
          await this.profileService.deleteRingtones([fileEntry.public_id]);
          this.loadRingtones();
          this.toast.show(_ti('public.message.delete_success'));
        } catch (error) {
          this.loggerService.error('Delete ringtone error: ', error);
          this.showErrorMessage(error, _ti('public.message.delete_failure'));
        }
      }
    );
  }
}
